<!-- PC 顶部导航：logo + 主入口 + 搜索框 + 用户区 -->
<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { showConfirmDialog } from "vant";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/user";
import { useDevice } from "@/composables/useDevice";
import { cdnImg } from "@/utils/image";
import { edition } from "@edition";
import { AVAILABLE_LOCALES, getLocale, setLocale, type AppLocale } from "@/i18n";

const { t } = useI18n({ useScope: "global" });
const router = useRouter();
const route = useRoute();
const user = useUserStore();
const { switchToMobile } = useDevice();

function onSwitchMobile() {
  switchToMobile();
  // 切到手机版后回首页，避免停在 PC 专有布局上
  router.replace("/home");
}
const keyword = ref("");
const dropdownOpen = ref(false);

// 语言选择（单语言版本不展示）
const showLang = AVAILABLE_LOCALES.length > 1;
const langOpen = ref(false);
const currentLocale = computed(() => getLocale());
const currentLangLabel = computed(
  () => AVAILABLE_LOCALES.find((l) => l.value === currentLocale.value)?.label ?? "",
);
function onPickLang(code: AppLocale): void {
  langOpen.value = false;
  if (code === getLocale()) return;
  setLocale(code);
  // 重新加载，让已展示的服务端内容按新语言重取（前后端语言一致）。
  window.location.reload();
}

function onSearch() {
  const kw = keyword.value.trim();
  if (!kw) return;
  router.push({ name: "Search", query: { q: kw } });
}

function navTo(name: string) {
  dropdownOpen.value = false;
  router.push({ name });
}

async function onLogout() {
  dropdownOpen.value = false;
  try {
    await showConfirmDialog({
      title: t("nav.logout"),
      message: t("nav.logoutConfirm"),
    });
  } catch {
    return;
  }
  user.logout();
  router.replace("/home");
}
</script>

<template>
  <header
    class="h-14 px-6 flex items-center gap-6 border-b border-line bg-panel"
  >
    <router-link to="/home" class="text-lg font-semibold text-brand">
      {{ t(edition.ui.brandKey) }}
    </router-link>

    <nav class="flex gap-4 text-sm text-muted">
      <router-link to="/home" active-class="text-white" class="hover:text-white">
        {{ t('nav.home') }}
      </router-link>
      <router-link
        v-if="!edition.review.enabled"
        to="/library"
        active-class="text-white"
        class="hover:text-white"
      >
        {{ t('nav.library') }}
      </router-link>
      <router-link
        v-else
        to="/subscription"
        active-class="text-white"
        class="hover:text-white"
      >
        {{ t('subscription.navTitle') }}
      </router-link>
    </nav>

    <!-- 搜索页自带大搜索框，顶栏这个隐藏，避免桌面端两个搜索框重叠 -->
    <div v-if="!edition.review.enabled && route.name !== 'Search'" class="flex-1 max-w-md">
      <van-search
        v-model="keyword"
        :placeholder="t('nav.searchPlaceholder')"
        shape="round"
        @search="onSearch"
      />
    </div>
    <!-- 隐藏搜索框时占位，保持右侧用户区靠右 -->
    <div v-else class="flex-1"></div>

    <!-- 语言选择 -->
    <div v-if="showLang" class="ml-auto relative">
      <button
        class="flex items-center gap-1 text-sm text-muted hover:text-white py-1 px-2 rounded"
        @click="langOpen = !langOpen"
      >
        <span>🌐</span>
        <span>{{ currentLangLabel }}</span>
        <span class="text-xs">▾</span>
      </button>
      <div
        v-if="langOpen"
        class="absolute right-0 top-full mt-1 w-44 max-h-80 overflow-auto bg-panel border border-line rounded-lg shadow-xl py-1 z-50"
        @click.stop
      >
        <button
          v-for="l in AVAILABLE_LOCALES"
          :key="l.value"
          class="w-full px-4 py-2 text-left text-sm hover:bg-bg/40 flex items-center justify-between gap-2"
          :class="l.value === currentLocale ? 'text-brand' : 'text-white'"
          @click="onPickLang(l.value)"
        >
          <span>{{ l.label }}</span>
          <span v-if="l.value === currentLocale">✓</span>
        </button>
      </div>
    </div>

    <div class="text-sm relative" :class="{ 'ml-auto': !showLang }">
      <template v-if="user.isLoggedIn">
        <button
          class="flex items-center gap-2 hover:text-brand py-1 px-2 rounded"
          @click="dropdownOpen = !dropdownOpen"
        >
          <img
            v-if="user.userInfo?.user_avatar"
            :src="cdnImg(user.userInfo.user_avatar)"
            class="w-7 h-7 rounded-full bg-line"
            alt=""
          />
          <span>{{ user.userInfo?.user_nickname || t('nav.me') }}</span>
          <span
            v-if="user.vipLevel === 'svip'"
            class="text-xs px-1 rounded bg-warn text-bg"
          >SVIP</span>
          <span
            v-else-if="user.vipLevel === 'vip'"
            class="text-xs px-1 rounded bg-blue-500 text-white"
          >VIP</span>
          <span v-else-if="user.isGuest" class="text-xs text-muted">{{ t('nav.guest') }}</span>
          <span class="text-xs text-muted">▾</span>
        </button>

        <!-- 下拉菜单 -->
        <div
          v-if="dropdownOpen"
          class="absolute right-0 top-full mt-1 w-56 bg-panel border border-line rounded-lg shadow-xl py-1 z-50"
          @click.stop
        >
          <button
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="navTo('Mine')"
          >
            <span>👤</span><span>{{ t('nav.profile') }}</span>
          </button>
          <button
            v-if="!edition.review.enabled"
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="navTo('MyGames')"
          >
            <span>🎮</span><span>{{ t('nav.myGames') }}</span>
          </button>
          <button
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="navTo('SignIn')"
          >
            <span>🎁</span><span>{{ t('nav.dailyCheckIn') }}</span>
          </button>
          <button
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="navTo('Subscription')"
          >
            <span>⭐</span><span>{{ user.isPremium ? t('nav.renewMembership') : t('nav.upgradeMembership') }}</span>
          </button>
          <div class="h-px bg-line my-1"></div>
          <button
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="navTo('Settings')"
          >
            <span>⚙️</span><span>{{ t('nav.settings') }}</span>
          </button>
          <button
            class="w-full px-4 py-2 text-left hover:bg-bg/40 flex items-center gap-2"
            @click="onSwitchMobile"
          >
            <span>📱</span><span>{{ t('nav.switchToMobile') }}</span>
          </button>
          <button
            class="w-full px-4 py-2 text-left hover:bg-err/20 text-err flex items-center gap-2"
            @click="onLogout"
          >
            <span>↪</span><span>{{ t('nav.logout') }}</span>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="flex items-center gap-3">
          <button
            class="text-xs text-muted hover:text-white"
            @click="onSwitchMobile"
          >
            📱 {{ t('nav.mobileVersion') }}
          </button>
          <button
            class="px-3 py-1 rounded bg-brand text-bg font-medium"
            @click="router.push({ name: 'Login', query: { redirect: route.fullPath } })"
          >
            {{ t('nav.login') }}
          </button>
        </div>
      </template>
    </div>

    <!-- 点空白处关下拉 -->
    <div
      v-if="dropdownOpen"
      class="fixed inset-0 z-40"
      @click="dropdownOpen = false"
    ></div>
    <div
      v-if="langOpen"
      class="fixed inset-0 z-40"
      @click="langOpen = false"
    ></div>
  </header>
</template>
